import { caseScenarioApiRequest } from './apiClient';
import { downloadCaseFile } from './criminalCaseRequests';
import type { ApiResult } from '../types';

export type CaseScenario = {
  id: string;
  title: string;
  description: string;
  /** DMS / stored file name when PDF is served via download API. */
  pdfFileName: string;
  /** Absolute or relative URL when API exposes a direct document link. */
  pdfUrl: string;
  hasPdf: boolean;
  active: boolean;
};

type ApiRow = Record<string, unknown>;

type MapCaseScenarioOptions = {
  /** `/active` already returns active scenarios with an uploaded PDF. */
  fromActiveList?: boolean;
};

function pickStr(o: ApiRow, ...keys: string[]): string {
  for (const k of keys) {
    const v = o[k];
    if (typeof v === 'string' && v.trim()) return v.trim();
    if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  }
  return '';
}

function pickBool(o: ApiRow, ...keys: string[]): boolean | undefined {
  for (const k of keys) {
    const v = o[k];
    if (typeof v === 'boolean') return v;
    if (typeof v === 'string') {
      const n = v.trim().toLowerCase();
      if (n === 'true' || n === 'active' || n === '1') return true;
      if (n === 'false' || n === 'inactive' || n === '0') return false;
    }
  }
  return undefined;
}

function nestedDocumentRow(row: ApiRow): ApiRow | null {
  for (const key of ['document', 'pdf_document', 'pdfDocument', 'scenario_document', 'scenarioDocument', 'attachment']) {
    const nested = row[key];
    if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
      return nested as ApiRow;
    }
  }
  return null;
}

function unwrapRows(data: unknown): ApiRow[] {
  if (Array.isArray(data)) return data as ApiRow[];
  if (!data || typeof data !== 'object') return [];
  const record = data as ApiRow;
  if (Array.isArray(record.content)) return record.content as ApiRow[];
  if (Array.isArray(record.data)) return record.data as ApiRow[];
  if (Array.isArray(record.items)) return record.items as ApiRow[];
  if (Array.isArray(record.case_scenarios)) return record.case_scenarios as ApiRow[];
  if (Array.isArray(record.caseScenarios)) return record.caseScenarios as ApiRow[];
  const nested = record.data;
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    const inner = nested as ApiRow;
    if (Array.isArray(inner.content)) return inner.content as ApiRow[];
    if (Array.isArray(inner.data)) return inner.data as ApiRow[];
    if (Array.isArray(inner.case_scenarios)) return inner.case_scenarios as ApiRow[];
    if (Array.isArray(inner.caseScenarios)) return inner.caseScenarios as ApiRow[];
  }
  return [];
}

function hasPdfIndicator(row: ApiRow): boolean {
  if (pickBool(row, 'has_pdf', 'hasPdf', 'pdf_uploaded', 'pdfUploaded', 'has_document', 'hasDocument') === true) {
    return true;
  }
  if (
    pickStr(
      row,
      'pdf_file_name',
      'pdfFileName',
      'file_name',
      'fileName',
      'document_file_name',
      'documentFileName',
      'uploaded_document_name',
      'uploadedDocumentName',
      'document_name',
      'documentName',
      'attachment_file_name',
      'attachmentFileName',
      'scenario_document_name',
      'scenarioDocumentName',
    )
  ) {
    return true;
  }
  if (pickStr(row, 'pdf_url', 'pdfUrl', 'document_url', 'documentUrl', 'file_url', 'fileUrl')) return true;
  if (pickStr(row, 'pdf_content', 'pdfContent', 'file_content', 'fileContent')) return true;
  if (pickStr(row, 'dms_file_id', 'dmsFileId', 'document_id', 'documentId', 'file_id', 'fileId')) return true;

  const nested = nestedDocumentRow(row);
  if (nested) return hasPdfIndicator(nested);

  return false;
}

function isActiveRow(row: ApiRow): boolean {
  const status = pickStr(row, 'status', 'status_code', 'statusCode', 'template_status', 'templateStatus');
  if (status) {
    const n = status.toUpperCase();
    if (n === 'ACTIVE' || n === 'A') return true;
    if (n === 'INACTIVE' || n === 'SUBMITTED' || n === 'DRAFT') return false;
  }
  const active = pickBool(row, 'active', 'is_active', 'isActive');
  return active !== false;
}

export function mapCaseScenarioRow(row: ApiRow, opts?: MapCaseScenarioOptions): CaseScenario | null {
  const nested = nestedDocumentRow(row);
  const merged = nested ? { ...row, ...nested } : row;

  const id = pickStr(merged, 'id', 'case_scenario_id', 'caseScenarioId', 'uuid');
  if (!id) return null;

  const title = pickStr(
    merged,
    'title',
    'case_scenario_title',
    'caseScenarioTitle',
    'case_scenario_name',
    'caseScenarioName',
    'name',
    'scenario_name',
    'scenarioName',
    'scenario_title',
    'scenarioTitle',
  );
  const description = pickStr(
    merged,
    'description',
    'case_scenario_description',
    'caseScenarioDescription',
    'details',
    'scenario_description',
    'scenarioDescription',
  );
  const pdfFileName = pickStr(
    merged,
    'pdf_file_name',
    'pdfFileName',
    'file_name',
    'fileName',
    'storage_file_name',
    'storageFileName',
    'document_file_name',
    'documentFileName',
    'uploaded_document_name',
    'uploadedDocumentName',
    'document_name',
    'documentName',
  );
  const pdfUrl = pickStr(merged, 'pdf_url', 'pdfUrl', 'document_url', 'documentUrl', 'file_url', 'fileUrl');
  const hasPdf = Boolean(pdfFileName) || (opts?.fromActiveList ? false : hasPdfIndicator(merged));
  const active = opts?.fromActiveList ? true : isActiveRow(merged);

  return { id, title: title || '—', description, pdfFileName, pdfUrl, hasPdf, active };
}

/** Active case scenarios with uploaded PDF — GET /v1/case-scenarios/active */
export async function listActiveCaseScenarios(signal?: AbortSignal): Promise<ApiResult<CaseScenario[]>> {
  const res = await caseScenarioApiRequest<unknown>('/active', {
    method: 'GET',
    signal,
    headers: { applicationCode: 'INTRANET', 'application-code': 'INTRANET' },
  });
  if (!res.ok) return { success: false, message: res.message };

  const items = unwrapRows(res.data)
    .map((row) => mapCaseScenarioRow(row, { fromActiveList: true }))
    .filter((row): row is CaseScenario => row !== null);

  return { success: true, data: items };
}

/** Fetch case-scenario PDF via CMS-CRC-6 (`downloadCaseFile` / DMS file download). */
export async function fetchCaseScenarioPdf(fileName: string): Promise<ApiResult<Blob>> {
  const key = fileName.trim();
  if (!key) return { success: false, message: 'Case scenario file name is required.' };
  return downloadCaseFile(key);
}
